/* 文章增强：生成目录、同步阅读位置、包装表格；订阅浮层复用 Ella 原有关闭按钮。 */
(() => {
  if (customElements.get('ggjia-article')) return;
  class GGJIAArticle extends HTMLElement {
    connectedCallback() {
      // 重连区块前清除监听器，兼容主题编辑器预览刷新。
      this.disconnectListeners();
      this.abortController = new AbortController();
      this.frame = null;
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initialize(), { once: true, signal: this.abortController.signal });
      } else this.initialize();
    }

    initialize() {
      const body = this.querySelector('[data-article-body]');
      if (!body) return;
      this.setupNewsletterPopupAutoClose();
      this.setupWebrexFAQ();

      // 横向溢出的表格可滑动且可键盘访问，不丢弃任何列。
      body.querySelectorAll('table').forEach((table, index) => {
        if (table.parentElement.classList.contains('ga-table-wrap')) return;
        const wrapper = document.createElement('div');
        wrapper.className = 'ga-table-wrap';
        wrapper.tabIndex = 0;
        wrapper.setAttribute('role', 'region');
        wrapper.setAttribute('aria-label', table.caption?.textContent.trim() || `Article table ${index + 1}. Scroll horizontally if needed.`);
        table.before(wrapper);
        wrapper.append(table);
      });
      const signal = this.abortController.signal;
      this.querySelectorAll('[data-toc-list]').forEach(list => {
        list.addEventListener('click', event => {
          const link = event.target.closest('a');
          if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
          const heading = document.getElementById(decodeURIComponent(link.hash.slice(1)));
          // FAQ 位于正文富文本之外；只允许定位到本文章已登记的标题。
          if (!heading || !this.headings.includes(heading)) return;
          event.preventDefault();
          // 先收起手机目录，再定位，避免折叠导致目标位置变化。
          const details = link.closest('[data-mobile-toc]');
          if (details) details.open = false;
          heading.setAttribute('tabindex', '-1');
          heading.focus({ preventScroll: true });
          heading.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
          history.replaceState(history.state, '', link.hash);
          this.setActive(heading.id);
        }, { signal });
      });
      // 滚动定位每动画帧最多运行一次，长章节不丢失当前状态。
      const schedule = () => {
        if (this.frame !== null) return;
        this.frame = requestAnimationFrame(() => { this.frame = null; this.updateActive(); });
      };
      window.addEventListener('scroll', schedule, { passive: true, signal });
      window.addEventListener('resize', schedule, { passive: true, signal });
      this.updateActive();
    }

    setupWebrexFAQ() {
      // 保留 Webrex 原生 details 与全部问答，不读取 JSON-LD 或创建第二份内容。
      const slot = this.querySelector('[data-article-apps]');
      this.faqInitialized ||= new WeakSet();
      this.faqFrame = null;
      const refresh = () => {
        const faqHeadings = [];
        slot?.querySelectorAll('.webrex-faq').forEach(faq => {
          const questions = faq.querySelectorAll('.ws-item details');
          // 只管理由本脚本设置的空状态，避免解除应用主动隐藏的非空区块。
          if (!questions.length) {
            if (!faq.hidden) { faq.dataset.gaEmptyFaq = 'true'; faq.hidden = true; }
            return;
          }
          if (faq.dataset.gaEmptyFaq === 'true') {
            faq.hidden = false;
            delete faq.dataset.gaEmptyFaq;
          }
          if (!this.faqInitialized.has(faq)) {
            if (slot.dataset.faqOpenFirst !== 'false' && !faq.querySelector('details[open]')) questions[0].open = true;
            this.faqInitialized.add(faq);
          }
          const heading = faq.querySelector(':scope > h2');
          if (heading?.textContent.trim() && !faq.hidden) faqHeadings.push(heading);
        });
        this.refreshTOC(faqHeadings);
      };
      refresh();
      if (!slot) return;
      // 仅观察应用插槽的内容变化，兼容延迟输出与编辑器重载；开合操作不会重建目录。
      this.faqObserver = new MutationObserver(() => {
        if (this.faqFrame !== null) return;
        this.faqFrame = requestAnimationFrame(() => { this.faqFrame = null; refresh(); });
      });
      this.faqObserver.observe(slot, { childList: true, subtree: true, characterData: true });
    }

    refreshTOC(faqHeadings = []) {
      // 目录仅增加 FAQ 总标题，不把每一道问题加入导航。
      const body = this.querySelector('[data-article-body]');
      this.headings = [...body.querySelectorAll('h2'), ...faqHeadings].filter(heading => heading.textContent.trim());
      this.headings.forEach((heading, index) => {
        if (!heading.id || document.getElementById(heading.id) !== heading) {
          const base = `${this.id || 'ggjia-article'}-section-${index + 1}`;
          let candidate = base;
          let suffix = 2;
          while (document.getElementById(candidate)) candidate = `${base}-${suffix++}`;
          heading.id = candidate;
        }
      });
      // 使用纯文本创建链接，保留原文章锚点，不把应用文本当作 HTML 插入。
      this.querySelectorAll('[data-toc-list]').forEach(list => {
        list.replaceChildren();
        this.headings.forEach(heading => {
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.href = `#${encodeURIComponent(heading.id)}`;
          link.textContent = faqHeadings.includes(heading) ? 'FAQ' : heading.textContent.trim();
          item.append(link);
          list.append(item);
        });
      });
      this.querySelectorAll('[data-mobile-toc], [data-desktop-toc]').forEach(toc => { toc.hidden = this.headings.length < 2; });
      this.tocLinks = [...this.querySelectorAll('[data-toc-list] a')];
      this.updateActive();
    }

    setupNewsletterPopupAutoClose() {
      // 仅处理当前文章页的 Ella 订阅成功浮层；正文订阅说明、评论和错误提示均不动。
      const popups = [...document.querySelectorAll('[data-newsletter-success-modal]')];
      if (!popups.length) return;
      this.newsletterPopupStates = new Map(popups.map(popup => [popup, { timer: null, handled: false }]));
      const visible = element => {
        if (!element?.isConnected || !element.getClientRects().length) return false;
        // 兼容主题以 display、visibility、opacity 或父容器状态控制浮层。
        for (let current = element; current; current = current.parentElement) {
          const style = getComputedStyle(current);
          if (current.hidden || current.getAttribute('aria-hidden') === 'true' || style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
        }
        return true;
      };
      const isSuccess = popup => visible(popup) && visible(popup.querySelector('.halo-modal-body.success')) && !visible(popup.querySelector('.halo-modal-body.error'));
      const sync = () => {
        this.newsletterPopupStates.forEach((state, popup) => {
          if (!isSuccess(popup)) {
            if (state.timer !== null) clearTimeout(state.timer);
            state.timer = null;
            state.handled = false;
            return;
          }
          // 重复样式变化不重置计时；手动关闭再打开时才重新计算 5 秒。
          if (state.timer !== null || state.handled) return;
          state.timer = setTimeout(() => {
            state.timer = null;
            state.handled = true;
            if (!isSuccess(popup)) return;
            // 复用主题关闭逻辑，避免只隐藏浮层却留下遮罩或滚动锁。
            popup.querySelector('button.close-modal, button.close')?.click();
          }, 5000);
        });
      };
      this.newsletterPopupObserver = new MutationObserver(sync);
      popups.forEach(popup => this.newsletterPopupObserver.observe(popup, { attributes: true, childList: true, subtree: true, attributeFilter: ['style', 'class', 'hidden', 'aria-hidden'] }));
      // 主题也可能通过 body/html 的类切换显示；只监听它们自身，不观察整页子树。
      [document.body, document.documentElement].forEach(element => this.newsletterPopupObserver.observe(element, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] }));
      sync();
    }

    updateActive() {
      if (!this.headings?.length) return;
      const offset = parseFloat(getComputedStyle(this).getPropertyValue('--ga-sticky-top')) || 160;
      let current = this.headings[0];
      for (const heading of this.headings) if (heading.getBoundingClientRect().top <= offset + 32) current = heading;
      this.setActive(current.id);
    }

    setActive(id) {
      this.tocLinks.forEach(link => {
        if (decodeURIComponent(link.hash.slice(1)) === id) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    }

    disconnectListeners() {
      this.abortController?.abort();
      if (this.frame != null) cancelAnimationFrame(this.frame);
      // 卸载时停止 FAQ 观察器及排队刷新，避免旧区块继续修改目录。
      this.faqObserver?.disconnect();
      if (this.faqFrame != null) cancelAnimationFrame(this.faqFrame);
      // 区块卸载或编辑器重载时取消旧计时，避免新浮层被上一轮任务关闭。
      this.newsletterPopupObserver?.disconnect();
      this.newsletterPopupStates?.forEach(state => { if (state.timer !== null) clearTimeout(state.timer); });
      this.newsletterPopupStates = null;
    }
    disconnectedCallback() { this.disconnectListeners(); }
  }
  customElements.define('ggjia-article', GGJIAArticle);
})();
